<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { api, type Job } from '@/api/client'
import type { ResourceRef } from '@/lib/resource-links'
import { cachedPreviewFetch } from '@/lib/preview-cache'

const props = defineProps<{ resourceRef: ResourceRef }>()

const loading = ref(true)
const error = ref<string | null>(null)
const job = ref<Job | null>(null)
let cancelled = false

const cacheKey = computed(() => `job:${props.resourceRef.id}`)

const dagRows = computed(() => {
  const tasks = job.value?.dag?.tasks
  if (!tasks) return []
  return Object.entries(tasks)
    .slice(0, 5)
    .map(([key, node]) => ({
      key,
      task: node.task,
      depends: node.depends_on?.join(', ') || '—',
    }))
})

onMounted(async () => {
  try {
    job.value = await cachedPreviewFetch(cacheKey.value, () =>
      api.get<Job>(`/jobs/${props.resourceRef.id}`),
    )
  } catch (err) {
    if (!cancelled) error.value = (err as Error).message
    job.value = null
  } finally {
    if (!cancelled) loading.value = false
  }
})

onUnmounted(() => { cancelled = true })
</script>

<template>
  <div class="p-2 text-xs space-y-1.5 max-w-[280px] min-w-0">
    <p class="font-medium break-words">{{ loading ? 'Loading…' : (job?.name ?? `Job ${resourceRef.id}`) }}</p>
    <table v-if="dagRows.length" class="w-full text-[10px] border-collapse">
      <thead>
        <tr class="text-muted-foreground">
          <th class="text-left font-normal pr-2">Task</th>
          <th class="text-left font-normal">Depends</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in dagRows" :key="row.key" class="font-mono">
          <td class="pr-2 py-0.5 truncate max-w-[100px]" :title="row.key">{{ row.key }}</td>
          <td class="text-muted-foreground truncate max-w-[120px]" :title="row.depends">{{ row.depends }}</td>
        </tr>
      </tbody>
    </table>
    <p v-else-if="error" class="text-destructive break-words">{{ error }}</p>
    <p v-else-if="!loading" class="text-muted-foreground">Job #{{ resourceRef.id }}</p>
  </div>
</template>
